import { useEffect, useState } from "react"

import { ComputeFacetsResponse } from "@vertesia/client"
import { CollectionItem } from "@vertesia/common"
import { ArrowLeft, FolderPlus, ListFilter } from "lucide-react"

import { CollectionDataTable } from "@components/datatables/CollectionDataTable"
import { CreateCollectionModal } from "@components/modals/CreateCollectionModal"

import { useUserSession } from "@session/UserSession"

import { Button } from "@ui/button"
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "@ui/dropdown-menu"
import { Input } from "@ui/input"

import { COLLECTIONS_FACETS as facets } from "@/constants/facets"
import { useUserCache } from "@/hooks/use-user-cache"
import { getOptionCount } from "@/lib/facet"
import { formatPropertyName } from "@/lib/properties"


const PAGE_SIZE = 200

enum FilterState {
    MAIN = "main",
}

export function CollectionsPage() {
    const { client, isLoading, project, account } = useUserSession()

    const { getUserDisplayName } = useUserCache(client)

    const [open, setOpen] = useState(false)

    const [searchTerm, setSearchTerm] = useState("")
    const [currentFilter, setCurrentFilter] = useState("main")
    const [options, setOptions] = useState<ComputeFacetsResponse>({})
    const [match, setMatch] = useState<Record<string, any>>({})
    const [filtersCount, setFiltersCount] = useState<number>(0)

    const [isSearching, setIsSearching] = useState(false)
    const [collections, setCollections] = useState<CollectionItem[]>([])

    const [currentPage, setCurrentPage] = useState(0)
    const [isLoadingMore, setIsLoadingMore] = useState(false)
    const [hasMoreDocuments, setHasMoreDocuments] = useState(true)

    const loadMoreDocuments = async () => {
        if (isLoadingMore || !hasMoreDocuments) {
            return false
        }
        await searchCollections(true)
        return hasMoreDocuments
    }

    const searchCollections = async (loadMore: boolean = false) => {
        setIsSearching(true)

        if (loadMore) {
            setIsLoadingMore(true)
        } else {
            setIsSearching(true)
            setCurrentPage(0)
            setCollections([])
            setHasMoreDocuments(true)
        }

        const updatedMatch = { ...match }
        for (const opt of Object.values(facets)) {
            if (match[opt.name] && opt.field !== opt.name) {
                updatedMatch[opt.field] = match[opt.name]
                delete updatedMatch[opt.name]
            }
        }

        const query = {
            name: searchTerm,
            match: updatedMatch,
        }

        const offset = loadMore ? currentPage * PAGE_SIZE : 0
        const page = loadMore ? currentPage + 1 : 0

        client.store.collections.computeListFacets({ facets, query }).then((response) => {
            if (response.total) {
                delete response.total
            }
            setOptions(response)
        })

        client.store.collections
            .search({ limit: PAGE_SIZE, offset, ...query })
            .then((response) => {
                const allDocuments = loadMore ? [...collections, ...response] : response
                setCollections(allDocuments)
                setCurrentPage(page)
                setHasMoreDocuments(allDocuments.length === PAGE_SIZE)
            })
            .finally(() => {
                setIsSearching(false)
                setIsLoadingMore(false)
            })
    }

    const handleBackToMain = () => {
        setCurrentFilter(FilterState.MAIN)
    }

    const handleFilterSelect = (event: React.MouseEvent, filter: string) => {
        event.stopPropagation()
        event.preventDefault()
        setCurrentFilter(filter)
    }

    const handleValueSelect = (filter: string, value: string) => {
        setMatch((prev) => ({
            ...prev,
            [filter]: value,
        }))
        setCurrentFilter(FilterState.MAIN)
    }

    const clearFilters = () => {
        if (currentFilter !== FilterState.MAIN) {
            setMatch((prev) => {
                const newMatch = { ...prev }
                delete newMatch[currentFilter]
                return newMatch
            })
        } else {
            setMatch({})
        }
        setCurrentFilter(FilterState.MAIN)
    }

    useEffect(() => {
        setFiltersCount(Object.keys(match).length)
        searchCollections()
    }, [match])

    useEffect(() => {
        if (!client || isLoading) {
            return
        }

        searchCollections()
    }, [client, project, account])

    return (
        <div className="w-full h-full flex flex-col gap-2">
            <div className="w-full flex flex-row gap-2 items-center justify-between p-2 border-b">
                <div className="text-2xl text-left font-semibold">Browse Collections</div>
                <div className="flex h-10 gap-2">
                    <Button onClick={() => setOpen(true)} className="h-full">
                        <FolderPlus />
                        Create Collection
                    </Button>
                </div>
            </div>
            <div className="w-full flex flex-row gap-2 items-center justify-between px-2">
                <Input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchCollections()} placeholder="Search Collection by Name" className="flex-grow" />
                <DropdownMenu>
                    <DropdownMenuTrigger asChild className="w-[150px]">
                        <Button className="text-left">
                            <ListFilter className="h-5 w-5" />
                            <span>Filters</span>
                            {filtersCount > 0 && <span className="text-xs text-blue-500">({filtersCount})</span>}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                        {currentFilter === "main" ? (
                            <>
                                <div className="flex items-center justify-between gap-2">
                                    <DropdownMenuLabel>Filters</DropdownMenuLabel>
                                    <div className="w-[40px]">
                                        {filtersCount !== 0 && (
                                            <Button variant="ghost" size="sm" onClick={clearFilters} className="p-1 italic font-light text-blue-400 hover:underline hover:cursor-pointer hover:text-blue-500">
                                                clear
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <DropdownMenuSeparator />
                                <div className="flex flex-col gap-1">
                                    {Object.keys(options).map((filterKey) => (
                                        <DropdownMenuItem key={filterKey} onClick={(event) => handleFilterSelect(event, filterKey)} className={`${Object.keys(match).includes(filterKey) ? "bg-blue-100 text-blue-800" : ""}`}>
                                            <div className="flex flex-row w-full items-center gap-2">
                                                <span className="flex-1 min-w-0 text-left truncate capitalize">{formatPropertyName(filterKey)}</span>
                                                <span className="shrink-0">({getOptionCount(filterKey, options)})</span>
                                            </div>
                                        </DropdownMenuItem>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="flex items-center justify-between gap-2">
                                    <Button variant="ghost" size="sm" onClick={handleBackToMain} className="p-1">
                                        <ArrowLeft />
                                    </Button>
                                    <DropdownMenuLabel className="capitalize">{formatPropertyName(currentFilter)}</DropdownMenuLabel>
                                    <div className="w-[40px]">
                                        {filtersCount !== 0 && (
                                            <Button variant="ghost" size="sm" onClick={clearFilters} className="p-1 italic font-light text-blue-400 hover:underline hover:cursor-pointer hover:text-blue-500">
                                                clear
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <DropdownMenuSeparator />
                                {Object.entries((options as any)[currentFilter]).map(([value, option]) => (
                                    <DropdownMenuItem key={value} onClick={() => handleValueSelect(currentFilter, (option as any)._id)} className={`${match[currentFilter] === (option as any)._id ? "bg-blue-100 text-blue-800" : ""}`}>
                                        <div className="flex flex-row w-full items-center gap-2">
                                            <span className="flex-1 min-w-0 text-left truncate">{currentFilter === "updated_by" || currentFilter === "created_by" ? getUserDisplayName((option as any)._id, undefined) : currentFilter === "dynamic" ? ((option as any)._id === true ? "Dynamic" : "Static") : (option as any)._id || value}</span>
                                            <span className="shrink-0">({(option as any).count})</span>
                                        </div>
                                    </DropdownMenuItem>
                                ))}
                            </>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
            <div className="flex-grow w-full overflow-auto px-2">
                <CollectionDataTable data={collections} loading={isSearching} loadingMore={isLoadingMore} hasMore={hasMoreDocuments} loadMore={loadMoreDocuments} onRefresh={searchCollections} />
            </div>
            <CreateCollectionModal open={open} setOpen={setOpen} onCreated={searchCollections} />
        </div>
    )
}
